import React, { useMemo } from "react";

import cn from "classnames";

import { Box, boxForwardRef } from "../../../helpers/common/Box/Box";
import { useMouseDownAnimation } from "../../../hooks/useMouseDownAnimation";
import { getChildrenRecord } from "../../../utils/react";
import { UniBoxAdditional } from "../../UniBox";

import type { ChipProps } from "./types";
import type { BoxComponentWithDefault } from "../../../helpers/common/Box/Box";

import classes from "./ChipChoice.styl";

const CHIP_CHOICE_DISPLAY_NAME = "ChipChoice";

/**
 * Чипсы, которые отвечают за выбор каких-либо опций.
 *
 * В качестве `as` ожидаем, что будет передан 'button', 'a' или компонент, реализующий функцию навигации (например, `RoutingLink` на большом вебе).
 * Более подробную информацию можно найти [тут](/docs/документация-разработчикам-дс-инструкция-по-разработке-полиморфных-компонентов--docs).
 *
 * <a href="https://www.figma.com/design/LYZAXE33pUg2cSL5mmUHYq/%F0%9F%8D%91-Buttons-%26-Tabs-%26-Chips?m=auto&node-id=2-2631&t=3ryRFv5yEABAv4kK-1" target="_blank">Спека</a>
 * @constructor
 */
export const ChipChoice = boxForwardRef<
    BoxComponentWithDefault<"button", ChipProps>
>(
    (
        {
            as = "button",
            customColorsStyles,
            type = "default",
            selected = false,
            view = "primary",
            "data-tsid": tsid,
            children,
            ...props
        },
        ref,
    ) => {
        const isSelected = selected;
        const isDropDownSelected = type === "dropdown" && isSelected;
        const isDropDown = type === "dropdown";
        const isTypeIcon = type === "icon";
        const isTypeIconSelected = type === "icon" && isSelected;
        const isTypeDefaultAndSelected =
            (type === "default" && isSelected) || type === "dropdown";

        const { Label, MainIcon, Counter, LeftIcon, RightIcon } = useMemo(
            () =>
                getChildrenRecord(children, "ChipChoice", {
                    Label: {},
                    MainIcon: {
                        props: {
                            size: "16",
                            icon: "glyph_filter_16_20",
                        },
                    },
                    Counter: {
                        props: {
                            size: "16",
                            stroke: isTypeIcon,
                        },
                    },
                    LeftIcon: {
                        props: {
                            size: "16",
                        },
                    },
                    RightIcon: {
                        props: {
                            size: "16",
                            icon: isDropDown
                                ? "glyph_down_16_20"
                                : "glyph_close_16_20",
                        },
                    },
                }),
            [children, isDropDown, isTypeIcon],
        );

        const { clicked, mousedownHandler } = useMouseDownAnimation();

        const containerClassName = isTypeIcon
            ? cn(
                  classes["chip-icon-container"],
                  classes[`__view-${view}`],
                  {
                      [classes[`__selected-${view}`]]: isSelected,
                  },
                  {
                      [classes[`__clicked`]]: clicked,
                  },
                  customColorsStyles,
              )
            : cn(
                  classes["chip-container"],
                  classes[`__view-${view}`],
                  {
                      [classes[`__selected-${view}`]]: isSelected,
                  },
                  {
                      [classes[`__clicked`]]: clicked,
                  },
                  customColorsStyles,
              );

        return (
            <Box
                as={as}
                {...props}
                type="button"
                className={containerClassName}
                data-tsid={tsid}
                data-uikit={CHIP_CHOICE_DISPLAY_NAME}
                onMouseDown={mousedownHandler}
                ref={ref}
            >
                {isTypeIcon && MainIcon}
                {!isTypeIcon && (
                    <span className={classes["left-icon"]}>{LeftIcon}</span>
                )}
                {!isTypeIcon && Label}
                {isTypeIconSelected && Counter && (
                    <UniBoxAdditional>
                        <UniBoxAdditional.SlotTopRight
                            customVarsStyles={classes["icon-counter"]}
                        >
                            {Counter}
                        </UniBoxAdditional.SlotTopRight>
                    </UniBoxAdditional>
                )}
                {isDropDownSelected && (
                    <span className={classes["counter"]}>{Counter}</span>
                )}
                {isTypeDefaultAndSelected && !isTypeIcon && (
                    <span className={classes["right-icon"]}>{RightIcon}</span>
                )}
            </Box>
        );
    },
);

ChipChoice.displayName = CHIP_CHOICE_DISPLAY_NAME;

export default ChipChoice;
